import { useCallback, useEffect, useState } from "react";
import { clone, first } from "lodash";
import { AnimationClip, Group, Quaternion, Vector3 } from "three";
import { applyProperties } from "../helpers/3d";

interface ISceneData {
    name: string;
    properties: {[ key: string ]: number};
}

interface TrueTouchedState {
    touched: true
    x: number
    y: number
    rotateStartPoint: Vector3
    rotateEndPoint: Vector3
    lastMoveTimestamp: Date
}

type TouchState = TrueTouchedState | {
    touched: false
}

const rotationSpeed = 0.05;
// const moveReleaseTimeDelta = 50;

export const useCsvScenes = (file: string) => {// @ts-ignore
    const isTouchDevice = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));
    const [animations, setAnimations] = useState<AnimationClip[]>();
    const [model, setModel] = useState<Group|undefined>(undefined);
    const [scenes, setScenes] = useState<ISceneData[]>([]);
    const [activeSceneIndex, setActiveSceneIndex] = useState<number>();
    const [touchState, setTouchState] = useState<TouchState>({ touched: false });

    useEffect(() => {
        // Load the CSV file
        fetch(file).then((response) => response.text())
            .then((contents) => {
                const lines = contents.split("\r\n");
                const rows = lines.map(line => line.split(';'));

                // Retrieve the names of the scenes and the count
                const firstRow: string[] = first(rows) as string[];
                firstRow.shift();
                const sceneNames = clone(firstRow);

                // Remove the first row
                rows.shift();

                // Set all new sceneData
                const newSceneData: ISceneData[] = [];
                sceneNames.forEach((sceneName, sceneIndex) => {
                    const filteredProperties: {[key: string]: number} = {};
                    rows.forEach((row) => {
                        const name = row[0];
                        const value = row[sceneIndex + 1];
                        if ((value ?? '').length > 0) {
                            filteredProperties[name] = parseFloat(value.split(',').join('.'));
                        }
                    });
                    newSceneData.push({
                        name: sceneName,
                        properties: filteredProperties,
                    });
                });

                setScenes(newSceneData);
            })
    }, [file]);


    const apply = useCallback(() => {
        const foundActiveScene = scenes.find((scene, index) => {
            return index === activeSceneIndex;
        });
        if (foundActiveScene && model && animations) {
            console.log('applying');
            applyProperties(model, foundActiveScene.properties, animations);
        }
    }, [scenes, activeSceneIndex, model, animations])

    useEffect(() => {
        apply();
    }, [apply, scenes, activeSceneIndex, model, animations]);

    // Default rotation
    useEffect(() => {
        if (model) {
            let active = true;
            const animate = () => {
                if (!active) {
                    return;
                }
                requestAnimationFrame(animate);

                (model.parent as Group).rotation.y += 0.005;
            }
            animate();
            return () => {
                active = false;
            }
        }
        return () => {};
    }, [model]);






    // User rotation
//     useEffect(() => {
//         if (model && touchState.touched) {
//             const { deltaX, deltaY } = touchState;
//
//
//             rotateEndPoint = projectOnTrackball(deltaX, deltaY);
//
//             const rotateQuaternion = rotateMatrix(rotateStartPoint, rotateEndPoint);
//             curQuaternion = cube.quaternion;
//             curQuaternion.multiplyQuaternions(rotateQuaternion, curQuaternion);
//             curQuaternion.normalize();
//             cube.setRotationFromQuaternion(curQuaternion);
//
//             rotateEndPoint = rotateStartPoint;
//
// //             const beforeX = model.rotation.x + 0;
// //             const beforeY = model.rotation.y + 0;
// //             model.rotation.y += deltaX / 100;
// //             model.rotation.x += deltaY / 100;
// //             (document.querySelector('#debug') as HTMLDivElement).innerHTML = `
// // x: ${model.rotation.x}<br />
// // y: ${model.rotation.y}<br />
// // deltaX: ${deltaX}<br />
// // deltaY: ${deltaY}<br />
// // beforeX: ${beforeX}<br />
// // beforeY: ${beforeY}<br />`;
//         } else if (model && !touchState.touched) {
//             model.quaternion.normalize();
//         }
//     }, [model, touchState, rotateMatrix]);

    useEffect(() => {
        const projectOnTrackball = (touchX: number, touchY: number) => {
            const mouseOnBall = new Vector3();

            const clamp = (value: number, min: number, max: number) => {
                return Math.min(Math.max(value, min), max);
            }

            const windowHalfX = window.innerWidth / 2;
            const windowHalfY = window.innerHeight / 2;
            mouseOnBall.set(
                clamp(touchX / windowHalfX, -1, 1), clamp(-touchY / windowHalfY, -1, 1),
                0.0
            );

            const length = mouseOnBall.length();

            if (length > 1.0) {
                mouseOnBall.normalize();
            } else {
                mouseOnBall.z = Math.sqrt(1.0 - length * length);
            }

            return mouseOnBall;
        }
        const rotateMatrix = (rotateStart: Vector3, rotateEnd: Vector3) => {
            const axis = new Vector3(),
                quaternion = new Quaternion();

            let angle = Math.acos(rotateStart.dot(rotateEnd) / rotateStart.length() / rotateEnd.length());

            if (angle) {
                axis.crossVectors(rotateStart, rotateEnd).normalize();
                angle *= rotationSpeed;
                quaternion.setFromAxisAngle(axis, angle);
            }
            return quaternion;
        }
        const handleTouchStart = ({clientX, clientY, touches, target}: any) => {
            const { tagName } = target;
            if (tagName !== 'CANVAS') {
                return;
            }
            const x = clientX ?? (touches[0] as any as Touch).clientX;
            const y = clientY ?? (touches[0] as any as Touch).clientY;
            const position = projectOnTrackball(0, 0)
            setTouchState({touched: true, x, y, rotateStartPoint: position, rotateEndPoint: position, lastMoveTimestamp: new Date()});
        };

        const handleTouchMove = ({clientX, clientY, touches}: any) => {
            if (!touchState.touched || !model) {
                return;
            }
            const x = clientX ?? (touches[0] as any as Touch).clientX;
            const y = clientY ?? (touches[0] as any as Touch).clientY;
            const deltaX = x - (touchState as TrueTouchedState).x;
            const deltaY = y - (touchState as TrueTouchedState).y;

            const rotateEndPoint = projectOnTrackball(deltaX, deltaY);

            const rotateQuaternion = rotateMatrix(touchState.rotateStartPoint, rotateEndPoint);
            const currentQuaternion = model.quaternion;
            currentQuaternion.multiplyQuaternions(rotateQuaternion, currentQuaternion);
            currentQuaternion.normalize();
            model.setRotationFromQuaternion(currentQuaternion);

            setTouchState((prevState) => {
                return {...prevState, lastMoveTimestamp: new Date() };
            });
        };

        const handleTouchEnd = ({clientX, clientY, touches}: any) => {
            if (!touchState.touched) {
                return;
            }
            setTouchState({touched: false});
        };

        if (isTouchDevice) {
            window.addEventListener('touchstart', handleTouchStart);
            window.addEventListener('touchend', handleTouchEnd);
            window.addEventListener('touchcancel', handleTouchEnd);
            window.addEventListener('touchmove', handleTouchMove);
        } else {
            window.addEventListener('mousedown', handleTouchStart);
            window.addEventListener('mouseup', handleTouchEnd);
            window.addEventListener('mousemove', handleTouchMove);
        }
        window.addEventListener('visibilitychange', handleTouchEnd);

        return () => {
            if (isTouchDevice) {
                window.removeEventListener('touchstart', handleTouchStart);
                window.removeEventListener('touchend', handleTouchEnd);
                window.removeEventListener('touchcancel', handleTouchEnd);
                window.removeEventListener('touchmove', handleTouchMove);
            } else {
                window.removeEventListener('mousedown', handleTouchStart);
                window.removeEventListener('mouseup', handleTouchEnd);
                window.removeEventListener('mousemove', handleTouchMove);
            }
            window.addEventListener('visibilitychange', handleTouchEnd);
        }
    }, [touchState, model, isTouchDevice]);

    return {
        setModel,
        setAnimations,
        activeSceneIndex,
        setActiveSceneIndex,
        apply,
    }
}


// function copyStuff() {
//     'use_strict';
//
//     var container;
//
//     var camera, scene, renderer;
//
//     var cube, plane;
//
//     var mouseDown = false;
//     var rotateStartPoint = new THREE.Vector3(0, 0, 1);
//     var rotateEndPoint = new THREE.Vector3(0, 0, 1);
//
//     var curQuaternion;
//     var windowHalfX = window.innerWidth / 2;
//     var windowHalfY = window.innerHeight / 2;
//     var rotationSpeed = 2;
//     var lastMoveTimestamp,
//         moveReleaseTimeDelta = 50;
//
//     var startPoint = {
//         x: 0,
//         y: 0
//     };
//
//     var deltaX = 0,
//         deltaY = 0;
//
//     var setup = function()
//     {
//
//         document.addEventListener('mousedown', onDocumentMouseDown, false);
//
//         window.addEventListener('resize', onWindowResize, false);
//
//         animate();
//     };
//
//     function onDocumentMouseDown(event)
//     {
//         event.preventDefault();
//
//         document.addEventListener('mousemove', onDocumentMouseMove, false);
//         document.addEventListener('mouseup', onDocumentMouseUp, false);
//
//         mouseDown = true;
//
//         startPoint = {
//             x: event.clientX,
//             y: event.clientY
//         };
//
//         rotateStartPoint = rotateEndPoint = projectOnTrackball(0, 0);
//     }
//
//     function onDocumentMouseMove(event)
//     {
//         deltaX = event.x - startPoint.x;
//         deltaY = event.y - startPoint.y;
//
//         handleRotation();
//
//         startPoint.x = event.x;
//         startPoint.y = event.y;
//
//         lastMoveTimestamp = new Date();
//     }
//
//     function onDocumentMouseUp(event)
//     {
//         if (new Date().getTime() - lastMoveTimestamp.getTime() > moveReleaseTimeDelta)
//         {
//             deltaX = event.x - startPoint.x;
//             deltaY = event.y - startPoint.y;
//         }
//
//         mouseDown = false;
//
//         document.removeEventListener('mousemove', onDocumentMouseMove, false);
//         document.removeEventListener('mouseup', onDocumentMouseUp, false);
//     }
//
//     function projectOnTrackball(touchX, touchY)
//     {
//         var mouseOnBall = new THREE.Vector3();
//
//         mouseOnBall.set(
//             clamp(touchX / windowHalfX, -1, 1), clamp(-touchY / windowHalfY, -1, 1),
//             0.0
//         );
//
//         var length = mouseOnBall.length();
//
//         if (length > 1.0)
//         {
//             mouseOnBall.normalize();
//         }
//         else
//         {
//             mouseOnBall.z = Math.sqrt(1.0 - length * length);
//         }
//
//         return mouseOnBall;
//     }
//
//     function rotateMatrix(rotateStart, rotateEnd)
//     {
//         var axis = new THREE.Vector3(),
//             quaternion = new THREE.Quaternion();
//
//         var angle = Math.acos(rotateStart.dot(rotateEnd) / rotateStart.length() / rotateEnd.length());
//
//         if (angle)
//         {
//             axis.crossVectors(rotateStart, rotateEnd).normalize();
//             angle *= rotationSpeed;
//             quaternion.setFromAxisAngle(axis, angle);
//         }
//         return quaternion;
//     }
//
//     function clamp(value, min, max)
//     {
//         return Math.min(Math.max(value, min), max);
//     }
//
//     function render()
//     {
//         if (!mouseDown)
//         {
//             var drag = 0.95;
//             var minDelta = 0.05;
//
//             if (deltaX < -minDelta || deltaX > minDelta)
//             {
//                 deltaX *= drag;
//             }
//             else
//             {
//                 deltaX = 0;
//             }
//
//             if (deltaY < -minDelta || deltaY > minDelta)
//             {
//                 deltaY *= drag;
//             }
//             else
//             {
//                 deltaY = 0;
//             }
//
//             handleRotation();
//         }
//
//         renderer.render(scene, camera);
//     }
//
//     var handleRotation = function()
//     {
//         rotateEndPoint = projectOnTrackball(deltaX, deltaY);
//
//         var rotateQuaternion = rotateMatrix(rotateStartPoint, rotateEndPoint);
//         curQuaternion = cube.quaternion;
//         curQuaternion.multiplyQuaternions(rotateQuaternion, curQuaternion);
//         curQuaternion.normalize();
//         cube.setRotationFromQuaternion(curQuaternion);
//
//         rotateEndPoint = rotateStartPoint;
//     };
// }
